import type { ValidationError } from "class-validator";
import type { Dispatch, SetStateAction } from "react";
import { LoginStep } from "../useLoginController";
import type { CustomerLoginCandidateDto } from "@Front/Api/Public/emailAuthCandidatesApi";
import type { EmailAuthCandidatesResponse } from "@Front/Api/Public/emailAuthCandidatesApi";
import type { CustomerAuthDisambiguation } from "@Front/Components/Layouts/Login/helpers/loginFolderUidHelpers";
import {
	applyCustomerLoginMulticandidateDecision,
	buildMailVerifySmsStepParams,
	runCustomerMailVerifySmsStep,
	type MailVerifySmsScopeOpts,
} from "./loginEmailSubmissionExecution";
import { buildActorFlowMulticandidateDecision } from "@Front/Components/Layouts/Login/hooks/helpers/loginActorCustomerChallengeHelpers";

const MAIL_SMS_FAILURE: ValidationError = {
	property: "email",
	constraints: { "400": "Impossible d’envoyer le code. Réessayez." },
};

async function runMailVerifySmsStepOrEmailError(
	scope: MailVerifySmsScopeOpts,
	email: string,
	authDisambiguation: CustomerAuthDisambiguation,
	setValidationErrors: (errors: ValidationError[]) => void,
): Promise<void> {
	try {
		await runCustomerMailVerifySmsStep(buildMailVerifySmsStepParams(email, scope, authDisambiguation));
	} catch {
		setValidationErrors([MAIL_SMS_FAILURE]);
	}
}

export async function runChooseCustomerActorChallenge(input: {
	snap: EmailAuthCandidatesResponse;
	email: string;
	scope: MailVerifySmsScopeOpts;
	setAuthDisambiguation: Dispatch<SetStateAction<CustomerAuthDisambiguation>>;
	setStep: (step: LoginStep) => void;
	setValidationErrors: (errors: ValidationError[]) => void;
	invitationScopeFromRouter: boolean;
	setCustomerTargetChoiceCandidates: Dispatch<SetStateAction<CustomerLoginCandidateDto[] | null>>;
}): Promise<void> {
	const { snap, email, scope, setAuthDisambiguation, setStep, setValidationErrors, invitationScopeFromRouter, setCustomerTargetChoiceCandidates } =
		input;
	if (snap.customerCandidates.length > 1) {
		const decision = buildActorFlowMulticandidateDecision(snap, invitationScopeFromRouter);
		if (decision.kind !== "mail_verify") {
			setAuthDisambiguation({ loginActor: "CUSTOMER" });
		}
		await applyCustomerLoginMulticandidateDecision(decision, email, {
			...scope,
			setStep,
			setValidationErrors,
			setAuthDisambiguation,
			setCustomerTargetChoiceCandidates,
		});
		return;
	}
	const row = snap.customerCandidates[0];
	if (!row) {
		return;
	}
	const next: CustomerAuthDisambiguation = {
		loginActor: "CUSTOMER",
		customerUid: row.customerUid,
		folderUid: row.folderUid,
		officeUid: row.officeUid,
	};
	setAuthDisambiguation(next);
	await runMailVerifySmsStepOrEmailError(scope, email, next, setValidationErrors);
}

export async function runSelectCustomerLoginTargetChallenge(input: {
	email: string;
	row: CustomerLoginCandidateDto;
	loginCandidates: EmailAuthCandidatesResponse | null;
	authDisambiguation: CustomerAuthDisambiguation;
	scope: MailVerifySmsScopeOpts;
	setAuthDisambiguation: Dispatch<SetStateAction<CustomerAuthDisambiguation>>;
	setValidationErrors: (errors: ValidationError[]) => void;
}): Promise<void> {
	const { email, row, loginCandidates, authDisambiguation, scope, setAuthDisambiguation, setValidationErrors } = input;
	const next = {
		...(loginCandidates?.actorChoiceRequired ? { loginActor: "CUSTOMER" as const } : {}),
		customerUid: row.customerUid,
		folderUid: row.folderUid,
		officeUid: row.officeUid,
	};
	const merged = { ...authDisambiguation, ...next };
	setAuthDisambiguation(merged);
	await runMailVerifySmsStepOrEmailError(scope, email, merged, setValidationErrors);
}
